import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, Tabs, DatePicker, Button, Space, Typography, Row, Col,
  message, Spin, Card
} from 'antd';
import { 
  ClockCircleOutlined, CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import { hourlyDataService } from '../../../services/api';
import HourlyMetricsComparisonTable from '../../HourlyReport/components/HourlyMetricsComparisonTable';
import HourlyDataChart from '../../HourlyReport/components/HourlyDataChart';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('tr');

const HourlyMetricsModal = ({ visible, onCancel, brand }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('comparison');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, 'day').startOf('day'),
    dayjs().endOf('day')
  ]);
  const [hourlyData, setHourlyData] = useState([]);
  const [previousHourlyData, setPreviousHourlyData] = useState([]);

  // Saatlik verileri yükle
  const loadHourlyData = useCallback(async () => {
    if (!brand?.id || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    try {
      // Türkiye saatinde gün başı/sonu yap, sonra UTC'ye çevir
      const startDate = dayjs.tz(dateRange[0], 'Europe/Istanbul').startOf('day').utc().toISOString();
      const endDate = dayjs.tz(dateRange[1], 'Europe/Istanbul').endOf('day').utc().toISOString();

      // Seçili dönem verilerini çek
      const data = await hourlyDataService.getHourlyData(
        brand.id,
        startDate,
        endDate
      );

      // Karşılaştırma için önceki dönem verilerini hesapla
      const daysDiff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') + 1;
      const previousStartDate = dayjs.tz(dateRange[0], 'Europe/Istanbul')
        .subtract(daysDiff, 'day')
        .startOf('day')
        .utc()
        .toISOString();
      const previousEndDate = dayjs.tz(dateRange[1], 'Europe/Istanbul')
        .subtract(daysDiff, 'day')
        .endOf('day')
        .utc()
        .toISOString();

      // Önceki dönem verilerini çek
      const previousData = await hourlyDataService.getHourlyData(
        brand.id,
        previousStartDate,
        previousEndDate
      );

      setHourlyData(data || []);
      setPreviousHourlyData(previousData || []);
    } catch (error) {
      console.error('Saatlik veriler yüklenirken hata:', error);
      message.error('Saatlik veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [brand?.id, dateRange]);

  useEffect(() => {
    if (visible) {
      loadHourlyData();
    }
  }, [visible, loadHourlyData]);

  // Hızlı tarih filtreleri
  const setQuickDateRange = (type) => {
    const today = dayjs();
    
    switch (type) {
      case 'today':
        setDateRange([today.startOf('day'), today.endOf('day')]);
        break;
      case 'yesterday':
        const yesterday = today.subtract(1, 'day');
        setDateRange([yesterday.startOf('day'), yesterday.endOf('day')]);
        break;
      case 'last7days':
        setDateRange([today.subtract(6, 'day').startOf('day'), today.endOf('day')]);
        break;
      case 'thisweek':
        setDateRange([today.startOf('week'), today.endOf('day')]);
        break;
      default:
        break;
    }
  };

  // Modal kapatılırken verileri temizle
  const handleCancel = () => {
    setHourlyData([]);
    setPreviousHourlyData([]);
    setActiveTab('comparison');
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ClockCircleOutlined />
          <span>{brand?.name || 'Marka'} - Saatlik Metrik Analizi</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width="95%"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose={true}
    >
      <div style={{ marginBottom: 16 }}>
        {/* Kontrol Paneli */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <div>
              <Text strong>Tarih Aralığı</Text>
              <RangePicker
                style={{ width: '100%', marginTop: 4 }}
                value={dateRange}
                onChange={setDateRange}
                format="DD.MM.YYYY"
                placeholder={['Başlangıç', 'Bitiş']}
                disabledDate={(current) => {
                  if (!current) return false;
                  
                  const today = dayjs();
                  const maxDaysAgo = 8;
                  
                  // Bugünden itibaren 8 günden eski tarihler disable
                  return current.isBefore(today.subtract(maxDaysAgo, 'day'), 'day') || 
                         current.isAfter(today, 'day'); // Gelecek tarihler de disable
                }}
              />
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div>
              <Text strong style={{ marginBottom: 8, display: 'block' }}>Hızlı Filtreler</Text>
              <Space wrap>
                <Button 
                  size="small" 
                  icon={<CalendarOutlined />}
                  onClick={() => setQuickDateRange('today')}
                >
                  Bugün
                </Button>
                <Button 
                  size="small"
                  onClick={() => setQuickDateRange('yesterday')}
                >
                  Dün
                </Button>
                <Button 
                  size="small"
                  onClick={() => setQuickDateRange('last7days')}
                >
                  Son 7 Gün
                </Button>
                <Button 
                  size="small"
                  onClick={() => setQuickDateRange('thisweek')}
                >
                  Bu Hafta
                </Button>
              </Space>
            </div>
          </Col>
        </Row>

        {/* Tab Paneli */}
        <Spin spinning={loading}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            style={{ minHeight: '600px' }}
          >
            <TabPane 
              tab="Saatlik Metrik Karşılaştırması" 
              key="comparison"
            >
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <HourlyMetricsComparisonTable 
                  data={hourlyData}
                  previousData={previousHourlyData}
                  loading={loading}
                />
              </div>
            </TabPane>
            
            <TabPane 
              tab="Saatlik Performans Grafiği" 
              key="chart"
            >
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    Saatlik Performans Grafiği
                  </Title>
                  <Text type="secondary">
                    {brand?.name || 'Marka'} - Gelir ve maliyetlerin saatlik dağılımı
                  </Text>
                </div>
                <HourlyDataChart 
                  data={hourlyData}
                  loading={loading}
                />
              </Card>
            </TabPane>
          </Tabs>
        </Spin>
      </div>
    </Modal>
  );
};

export default HourlyMetricsModal;